import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, Droplets, Sun, Shield, Clock, Layers, Sparkles,
  ArrowRight, ArrowLeft, ShoppingBag, ChevronRight, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  sun: Sun, droplets: Droplets, shield: Shield, clock: Clock,
  layers: Layers, sparkles: Sparkles,
};

function CategoryCard({ cat, onClick }) {
  const Icon = ICON_MAP[cat.icon] || BookOpen;
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl p-6 border border-[var(--sage)]/15 hover:border-[var(--violet)]/30 hover:shadow-lg transition-all text-left group"
      data-testid={`skinlab-cat-${cat.id}`}
    >
      <div className="w-14 h-14 rounded-2xl bg-[var(--violet-light)] flex items-center justify-center mb-4 group-hover:bg-[var(--violet)] transition-colors">
        <Icon className="h-7 w-7 text-[var(--violet)] group-hover:text-white transition-colors" />
      </div>
      <h3 className="text-base font-bold text-[var(--dark)] mb-1">{cat.name}</h3>
      <p className="text-xs text-[var(--dark)]/50 mb-3 leading-relaxed">{cat.description}</p>
      <span className="inline-flex items-center text-xs font-semibold text-[var(--violet)] group-hover:gap-2 transition-all">
        Ver rutina <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
      </span>
    </button>
  );
}

function CategoryDetail({ categoryId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/skinlab/${categoryId}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [categoryId]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[var(--violet)] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const Icon = ICON_MAP[data.icon] || BookOpen;

  return (
    <div data-testid="skinlab-detail">
      {/* Header */}
      <button onClick={() => navigate('/educacion')} className="flex items-center gap-2 text-xs text-[var(--dark)]/40 hover:text-[var(--violet)] mb-6 transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Volver a categorias
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-[var(--violet)] flex items-center justify-center flex-shrink-0">
          <Icon className="h-8 w-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-[var(--dark)]">{data.name}</h2>
          <p className="text-sm text-[var(--dark)]/50">{data.description}</p>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4 mb-12">
        {data.steps?.map((step, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-[var(--sage)]/15" data-testid={`step-${i}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[var(--violet)] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
              <h3 className="text-base font-bold text-[var(--dark)]">{step.title}</h3>
            </div>
            <p className="text-sm text-[var(--dark)]/60 leading-relaxed pl-11">{step.content}</p>
          </div>
        ))}
      </div>

      {/* Recommended Products */}
      {data.recommended_products?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-[var(--dark)]">Productos Recomendados</h3>
              <p className="text-xs text-[var(--dark)]/40">Seleccionados automaticamente para {data.name.toLowerCase()}</p>
            </div>
            <Link to="/tienda"><Button variant="outline" size="sm" className="text-xs border-[var(--sage)]/30">Ver todos <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.recommended_products.map(p => {
              const hasDiscount = p.discount_price && p.discount_price < p.price;
              const price = hasDiscount ? p.discount_price : p.price;
              return (
                <div key={p.id} className="bg-white rounded-xl overflow-hidden border border-[var(--sage)]/15 hover:shadow-md transition-shadow group" data-testid={`skinlab-product-${p.id}`}>
                  <Link to={`/producto/${p.id}`} className="block aspect-square overflow-hidden bg-[var(--cream)]">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </Link>
                  <div className="p-3 space-y-1.5">
                    <p className="text-[10px] text-[var(--violet)] font-semibold tracking-wider uppercase">{p.brand}</p>
                    <Link to={`/producto/${p.id}`}><h4 className="text-xs font-semibold text-[var(--dark)] line-clamp-2 hover:text-[var(--violet)] min-h-[32px]">{p.name}</h4></Link>
                    {p.benefits?.length > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-[var(--dark)]/30">
                        <Check className="h-3 w-3 text-[var(--sage-dark)]" /> {p.benefits[0]}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <div>
                        {hasDiscount && <span className="text-[10px] text-[var(--dark)]/25 line-through mr-1">${p.price?.toFixed(2)}</span>}
                        <span className={`text-sm font-bold ${hasDiscount ? 'text-[var(--violet)]' : 'text-[var(--dark)]'}`}>${price?.toFixed(2)}</span>
                      </div>
                      <Button size="sm" className="h-7 px-2 bg-[var(--dark)] hover:bg-[var(--violet)] text-white rounded-lg text-[10px]" onClick={() => addItem(p)} data-testid={`skinlab-add-${p.id}`}>
                        <ShoppingBag className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EducationPage() {
  const { categoryId } = useParams();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCat, setSelectedCat] = useState(categoryId || null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/skinlab/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/education`).then(r => setArticles(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (categoryId) setSelectedCat(categoryId); }, [categoryId]);

  const selectCategory = (id) => {
    setSelectedCat(id);
    navigate(`/educacion/${id}`);
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="education-page">
      <div className="bg-[var(--violet)] text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white border-0 mb-4">Tu guia de skincare coreano</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Aura Skin Lab</h1>
          <p className="text-base lg:text-lg text-white/70 max-w-2xl mx-auto">
            Rutinas personalizadas, ingredientes clave y productos recomendados para cada tipo de piel.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {selectedCat ? (
          <CategoryDetail categoryId={selectedCat} />
        ) : (
          <>
            {/* Category Navigation */}
            <div className="mb-16">
              <h2 className="text-xl font-bold text-[var(--dark)] mb-2">Elige tu tipo de piel</h2>
              <p className="text-sm text-[var(--dark)]/40 mb-6">Selecciona tu preocupacion y te daremos una rutina personalizada con productos recomendados.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map(cat => (
                  <CategoryCard key={cat.id} cat={cat} onClick={() => selectCategory(cat.id)} />
                ))}
              </div>
            </div>

            {/* Educational Articles */}
            {articles.length > 0 && (
              <div className="mb-16">
                <h2 className="text-xl font-bold text-[var(--dark)] mb-6 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-[var(--violet)]" /> Guias Educativas
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {articles.map(a => (
                    <div key={a.id} className="bg-white rounded-2xl overflow-hidden border border-[var(--sage)]/15 hover:shadow-md transition-shadow" data-testid={`edu-card-${a.id}`}>
                      {a.image && <div className="h-48 overflow-hidden"><img src={a.image} alt={a.title} className="w-full h-full object-cover" /></div>}
                      <div className="p-6">
                        <h3 className="text-base font-bold text-[var(--dark)] mb-2">{a.title}</h3>
                        <p className="text-sm text-[var(--dark)]/50 line-clamp-3 leading-relaxed">{a.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="text-center bg-[var(--sage-light)] rounded-3xl p-10">
              <h2 className="text-2xl font-bold text-[var(--dark)] mb-4">No sabes tu tipo de piel?</h2>
              <p className="text-sm text-[var(--dark)]/50 mb-6 max-w-md mx-auto">Nuestro quiz te ayudara a descubrirlo en solo 5 preguntas rapidas.</p>
              <Link to="/quiz"><Button className="bg-[var(--violet)] text-white rounded-xl px-8 h-11" data-testid="skinlab-quiz-btn">Hacer el Skin Quiz <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
